if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // ギャラリーナビゲーションの矢印ボタンにイベントを追加
        this.initGalleryNavigation();
      }

      initGalleryNavigation() {
        const navigation = this.elements.viewer?.querySelector('.product-gallery-navigation');
        if (!navigation) return;

        const prevButton = navigation.querySelector('button[name="previous"]');
        const nextButton = navigation.querySelector('button[name="next"]');

        // captureフェーズでイベントを捕捉し、SliderComponentのイベントより先に実行
        if (prevButton) {
          prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.navigateToAdjacentMedia('previous');
          }, true); // captureフェーズ
        }

        if (nextButton) {
          nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.navigateToAdjacentMedia('next');
          }, true); // captureフェーズ
        }
      }

      navigateToAdjacentMedia(direction) {
        // メインギャラリーのスライダー要素を取得
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider) return;

        // メインギャラリー内の.product__media-item要素のみを取得（モーダル用要素を除外）
        const allMediaElements = Array.from(slider.querySelectorAll('.product__media-item[data-media-id]'));
        const activeMedia = slider.querySelector('.product__media-item[data-media-id].is-active');
        
        if (!activeMedia || allMediaElements.length <= 1) return;

        // 現在のアクティブな要素のインデックスを取得
        const currentIndex = allMediaElements.indexOf(activeMedia);
        if (currentIndex === -1) return;

        // 次の/前の要素を探す
        let targetIndex;

        if (direction === 'next') {
          targetIndex = currentIndex < allMediaElements.length - 1 ? currentIndex + 1 : 0;
        } else {
          targetIndex = currentIndex > 0 ? currentIndex - 1 : allMediaElements.length - 1;
        }

        const targetMedia = allMediaElements[targetIndex];
        if (targetMedia && targetMedia !== activeMedia) {
          const mediaId = targetMedia.dataset.mediaId;
          this.setActiveMedia(mediaId, false);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // メインギャラリーのスライダー要素を取得
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider) return;

        // メインギャラリー内の.product__media-item要素のみを対象にする
        const activeMedia =
          slider.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          slider.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        
        // メインギャラリー内のすべての.product__media-item要素からis-activeクラスを削除
        slider.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        } else {
          // サムネイルがない場合でも、ライブリージョンを更新
          const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
          if (slider) {
            const visibleMedia = Array.from(slider.querySelectorAll('.product__media-item[data-media-id]'));
            const mediaPosition = visibleMedia.indexOf(activeMedia) + 1;
            this.announceLiveRegion(activeMedia, mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
